import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash/get';

import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { FormattedMessage } from '../../util/reactIntl';
import { LinkTabNavHorizontal } from '../../components';
import { USER_TYPE } from '../../util/types';

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink } = props;
  const classes = classNames(rootClassName || css.root, className);
  const { currentUser } = useSelector(state => state.user);
  const userType = get(currentUser, 'attributes.profile.metadata.userType', null)
  const isProducer = userType === USER_TYPE.PRODUCER;
  const isAdmin = userType === USER_TYPE.ADMIN;
  const yourListingsLink =
    isProducer || isAdmin
      ? [
          {
            text: <FormattedMessage id="UserNav.yourListings" />,
            selected: currentPage === 'ManageListingsPage',
            linkProps: {
              name: 'ManageListingsPage',
            },
          },
        ]
      : [];

  const tabs = [
    ...yourListingsLink,
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.favorites" />,
      selected: currentPage === 'FavoritesPage',
      linkProps: {
        name: 'FavoritesPage',
        params: {
          userId: currentUser?.id.uuid || 'no-slug'
        }
      },
    },
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal className={classes} tabRootClassName={css.tab} tabs={tabs} skin="dark" />
  );
};

export default UserNav;
